import type { CloudWord, DriverRow, Sentiment } from "../Types/ListeningReportTypes";

const DRIVER_SORT_KEYS = ["neg", "pos", "net"] as const;
export type DriverSortKey = (typeof DRIVER_SORT_KEYS)[number];
export const isDriverSortKey = (value: unknown): value is DriverSortKey =>
  DRIVER_SORT_KEYS.some(key => key === value);
export type DriverStatus = "strength" | "watch" | "risk";

const STRENGTH_MIN_NET = 5;
const WATCH_MIN_NET = -3;
const MAX_WORD_EXAMPLES = 3;

export const getDriverNet = (driver: DriverRow) => driver.positive - driver.negative;

export const getDriverStatus = (driver: DriverRow): DriverStatus => {
  const net = getDriverNet(driver);
  if (net > STRENGTH_MIN_NET) {
    return "strength";
  }
  return net >= WATCH_MIN_NET ? "watch" : "risk";
};

export const sortDrivers = (drivers: DriverRow[], key: DriverSortKey): DriverRow[] =>
  [...drivers].sort((a, b) => {
    if (key === "pos") {
      return b.positive - a.positive;
    }
    if (key === "net") {
      return getDriverNet(a) - getDriverNet(b);
    }
    return b.negative - a.negative || a.positive - b.positive;
  });

export const formatSignedNumber = (value: number, decimals = 0) => {
  const text = Math.abs(value).toFixed(decimals);
  if (value > 0) {
    return `+${text}`;
  }
  return value < 0 ? `−${text}` : text;
};

export const getSignedTone = (value: number): "pos" | "neg" | undefined => {
  if (value > 0) {
    return "pos";
  }
  return value < 0 ? "neg" : undefined;
};

/** Alternates words around the centre so the biggest ones sit in the middle of the cloud. */
export const orderCloudWords = (words: CloudWord[]): CloudWord[] =>
  [...words]
    .sort((a, b) => b.weight - a.weight)
    .reduce<CloudWord[]>((ordered, word, index) => {
      if (index % 2) {
        ordered.push(word);
      } else {
        ordered.unshift(word);
      }
      return ordered;
    }, []);

export const getTopWords = (words: CloudWord[], sentiment: Sentiment, count = 5): CloudWord[] =>
  words.filter(word => word.sentiment === sentiment).slice(0, count);

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const getWordPattern = (word: string): RegExp => {
  const source = word === "color" ? "colou?r" : escapeRegExp(word);
  return new RegExp(`\\b(${source})\\b`, "i");
};

export const findWordExamples = (word: string, comments: string[]): string[] => {
  const pattern = getWordPattern(word);
  return comments.filter(comment => pattern.test(comment)).slice(0, MAX_WORD_EXAMPLES);
};

/** Splits a comment so the odd entries are the occurrences of the word. */
export const splitByWord = (text: string, word: string): string[] =>
  text.split(getWordPattern(word));
